import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.14 },
  }),
};

const HERO_BG =
  "https://images.unsplash.com/photo-1776850344650-eff188096f02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbWluaW1hbGlzdCUyMGFyY2hpdGVjdHVyZSUyMGdvbGR8ZW58MHx8fHwxNzg2MzYxOTUwfDA&ixlib=rb-4.1.0&q=85";

export const Hero = ({ onCta }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative min-h-screen w-full overflow-hidden flex items-center"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0a0a0f]/85" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#0a0a0f]/40 to-[#0a0a0f]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f] via-transparent to-transparent" />
      </motion.div>

      {/* Ambient gold glow */}
      <div className="absolute -top-40 right-0 w-[38rem] h-[38rem] rounded-full bg-[#c9a84c]/10 blur-[130px] z-0" />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.35 }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="h-px w-12 bg-[#c9a84c]" />
          <span className="font-body text-xs tracking-[0.32em] uppercase text-[#c9a84c]">
            Est. Bankstown NSW · Australia-wide
          </span>
        </motion.div>

        <h1 className="font-display font-light tracking-tighter text-[#f8f9fa] leading-[0.92] text-5xl sm:text-6xl lg:text-8xl max-w-5xl">
          {["Premium accounting", "for those who", "expect more."].map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                variants={line}
                custom={i}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {i === 2 ? <span className="italic text-[#c9a84c]">{t}</span> : t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 1.15 }}
          className="font-body text-base sm:text-lg text-[#9ba1a6] font-light max-w-xl mt-10 leading-relaxed"
        >
          Discreet, meticulous tax and advisory for high-net-worth individuals and
          business owners. Premium Accounting. Exceptional Results.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 1.35 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <button
            onClick={onCta}
            data-testid="hero-cta-button"
            className="group relative inline-flex items-center gap-3 font-body text-sm tracking-wide px-8 py-4 rounded-full bg-[#c9a84c] text-[#0a0a0f] font-medium overflow-hidden"
          >
            <span className="relative z-10">Book a Consultation</span>
            <ArrowDownRight size={18} className="relative z-10 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            <span className="absolute inset-0 bg-[#e0c268] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
          </button>
          <span className="font-body text-sm text-[#9ba1a6]">
            or call <a href="tel:+61459646667" data-testid="hero-phone-link" className="text-[#e8d9a8] hover:text-[#c9a84c] transition-colors">0459 646 667</a>
          </span>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#9ba1a6]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-[#c9a84c] to-transparent"
        />
      </motion.div>
    </section>
  );
};
